import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiPlus, FiMinus, FiTrash2, FiShoppingBag, FiArrowRight } from 'react-icons/fi';
import { useCart } from '../../contexts/CartContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const CartSidebar: React.FC = () => {
  const { 
    items, 
    isOpen, 
    subtotal, 
    tax, 
    shipping, 
    total, 
    itemCount,
    toggleCart, 
    removeItem, 
    updateQuantity,
    clearCart,
    proceedToCheckout,
    isLoading,
    openCheckout
  } = useCart();
  
  console.log('🛒 CartSidebar render:', { isOpen, itemCount, items: items.length });
  
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleCheckout = async () => {
    if (!isAuthenticated) {
      toggleCart();
      navigate('/login?redirect=checkout');
      return;
    }
    try {
      // Validate inventory but open modal instead of navigating
      await proceedToCheckout();
      toggleCart();
      // Defer opening the global checkout modal slightly to allow sidebar to close smoothly
      setTimeout(() => openCheckout(), 50);
    } catch (error) {
      console.error('Checkout error:', error);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  return (
    <>
      {/* Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-[9998]"
            onClick={toggleCart}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl z-[9999] flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <FiShoppingBag size={24} className="text-blue-600" />
                <h2 className="text-xl font-semibold text-gray-800">
                  Carrito ({itemCount})
                </h2>
              </div>
              <button
                onClick={toggleCart}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <FiX size={24} className="text-gray-600" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 flex flex-col">
              {items.length === 0 ? (
                // Empty cart
                <div className="flex-1 flex items-center justify-center p-6">
                  <div className="text-center">
                    <FiShoppingBag size={64} className="text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-800 mb-2">
                      Tu carrito está vacío
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Agrega productos para comenzar tu compra
                    </p>
                    <button
                      onClick={toggleCart}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
                    >
                      Seguir comprando
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Items list */}
                  <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    {items.map((item) => (
                      <motion.div
                        key={item.id}
                        layout
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="bg-gray-50 rounded-lg p-4"
                      >
                        <div className="flex gap-4">
                          {/* Image */}
                          <div className="w-16 h-16 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                            <img
                              src={item.image}
                              alt={item.name}
                              className="w-full h-full object-cover"
                            />
                          </div>

                          {/* Details */}
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-gray-800 text-sm line-clamp-2">
                              {item.name}
                            </h4>
                            <p className="text-xs text-gray-600 mt-1">
                              {item.partner}
                            </p>
                            
                            {/* Price */}
                            <div className="flex items-center gap-2 mt-2">
                              <span className="font-semibold text-blue-600">
                                {formatPrice(item.price)}
                              </span>
                              {item.originalPrice && (
                                <span className="text-xs text-gray-500 line-through">
                                  {formatPrice(item.originalPrice)}
                                </span>
                              )}
                            </div>

                            {/* Stock warning */}
                            {item.quantity >= item.maxQuantity && (
                              <p className="text-xs text-orange-600 mt-1">
                                Stock limitado: {item.maxQuantity} disponibles
                              </p>
                            )}
                          </div>

                          {/* Remove button */}
                          <button
                            onClick={() => removeItem(item.id)}
                            className="p-1 hover:bg-red-100 rounded text-red-500 transition-colors flex-shrink-0"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </div>

                        {/* Quantity controls */}
                        <div className="flex items-center justify-between mt-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              disabled={item.quantity <= 1}
                              className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              <FiMinus size={14} />
                            </button>
                            
                            <span className="w-12 text-center font-medium">
                              {item.quantity}
                            </span>
                            
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              disabled={item.quantity >= item.maxQuantity}
                              className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              <FiPlus size={14} />
                            </button>
                          </div>

                          {/* Subtotal */}
                          <span className="font-semibold text-gray-800">
                            {formatPrice(item.price * item.quantity)}
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Summary */}
                  <div className="border-t border-gray-200 p-6 space-y-4">
                    {/* Clear cart button */}
                    <button
                      onClick={clearCart}
                      className="w-full text-sm text-red-600 hover:text-red-700 transition-colors"
                    >
                      Vaciar carrito
                    </button>

                    {/* Totals */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Subtotal:</span>
                        <span>{formatPrice(subtotal)}</span>
                      </div>
                      
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Impuestos:</span>
                        <span>{formatPrice(tax)}</span>
                      </div>
                      
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Envío:</span>
                        <span>{shipping === 0 ? 'Gratis' : formatPrice(shipping)}</span>
                      </div>
                      
                      <div className="border-t border-gray-200 pt-2">
                        <div className="flex justify-between font-semibold text-lg">
                          <span>Total:</span>
                          <span className="text-blue-600">{formatPrice(total)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Free shipping notice */}
                    {subtotal < 500 && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-sm text-blue-700">
                          💡 Agrega {formatPrice(500 - subtotal)} más para envío gratis
                        </p>
                      </div>
                    )}

                    {/* Checkout button */}
                    <button
                      onClick={handleCheckout}
                      disabled={isLoading}
                      className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      {isLoading ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          {isAuthenticated ? 'Proceder al checkout' : 'Iniciar sesión para continuar'}
                          <FiArrowRight size={18} />
                        </>
                      )}
                    </button>

                    {/* Continue shopping */}
                    <button
                      onClick={toggleCart}
                      className="w-full text-blue-600 hover:text-blue-700 py-2 transition-colors"
                    >
                      Seguir comprando
                    </button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CartSidebar;
